import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Eye, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import {
  gatewayStatusLabel,
  paymentService,
  paymentStatusLabel,
  type PaymentRow,
  type ZarinpalUnverifiedItem,
} from '../services/paymentService';
import { fieldClass, formatDateTime, formatMoney, labelClass } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-rose-100 text-rose-800',
};

function statusBadge(status: string) {
  const cls = statusColors[status] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${cls}`}>
      {paymentStatusLabel[status] || status || '—'}
    </span>
  );
}

function gatewayBadge(status: string) {
  if (!status) return <span className="text-slate-400">—</span>;
  return (
    <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
      {gatewayStatusLabel[status] || status}
    </span>
  );
}

export function PaymentsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [detail, setDetail] = useState<PaymentRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [unverifiedOpen, setUnverifiedOpen] = useState(false);
  const [unverified, setUnverified] = useState<ZarinpalUnverifiedItem[]>([]);
  const [unverifiedLoading, setUnverifiedLoading] = useState(false);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentService.list({
        page,
        limit,
        q: q.trim() || undefined,
        status: status || undefined,
        provider: provider || undefined,
      });
      setRows(res.data ?? []);
      setTotalPages(Math.max(1, res.totalPages || 1));
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت پرداخت‌ها');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, q, status, provider]);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = async (row: PaymentRow) => {
    try {
      const full = await paymentService.get(row.id);
      setDetail(full);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    }
  };

  const inquire = async (id: string) => {
    setBusy(true);
    try {
      const res = await paymentService.inquire(id);
      setDetail(res.payment);
      toast.success(
        res.inquiry.status
          ? `وضعیت زرین‌پال: ${gatewayStatusLabel[res.inquiry.status] || res.inquiry.status}`
          : 'استعلام انجام شد',
      );
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'استعلام ناموفق بود');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (id: string) => {
    if (!window.confirm('تأیید نهایی این پرداخت در زرین‌پال انجام شود؟')) return;
    setBusy(true);
    try {
      const res = await paymentService.verify(id);
      setDetail(res.payment);
      toast.success(res.alreadyVerified ? 'قبلاً تأیید شده بود' : 'پرداخت تأیید شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'تأیید ناموفق بود');
    } finally {
      setBusy(false);
    }
  };

  const loadUnverified = async () => {
    setUnverifiedOpen(true);
    setUnverifiedLoading(true);
    try {
      const items = await paymentService.listUnverified();
      setUnverified(items);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت لیست تأییدنشده');
      setUnverified([]);
    } finally {
      setUnverifiedLoading(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">پرداخت‌ها</h1>
          <p className="mt-1 text-sm text-slate-500">
            لجر پرداخت‌های آنلاین — وضعیت، تاریخ، Authority، RefID و اطلاعات زرین‌پال
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void loadUnverified()}
            className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-800 hover:bg-amber-100"
          >
            <ShieldCheck className="h-4 w-4" />
            تأییدنشده‌های زرین‌پال
          </button>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            به‌روزرسانی
          </button>
        </div>
      </div>

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-4">
        <div className="md:col-span-2">
          <label className={labelClass}>جستجو</label>
          <div className="relative">
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              className={`${fieldClass} pr-9`}
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Authority، RefID، کد سفارش، کارت…"
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>وضعیت</label>
          <select
            className={fieldClass}
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">همه</option>
            <option value="pending">در انتظار</option>
            <option value="completed">موفق</option>
            <option value="failed">ناموفق</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>درگاه</label>
          <select
            className={fieldClass}
            value={provider}
            onChange={(e) => {
              setPage(1);
              setProvider(e.target.value);
            }}
          >
            <option value="">همه</option>
            <option value="zarinpal">زرین‌پال</option>
            <option value="mock">Mock</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="admin-table-scroll">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-semibold">#</th>
                <th className="px-4 py-3 text-right font-semibold">سفارش</th>
                <th className="px-4 py-3 text-right font-semibold">مبلغ</th>
                <th className="px-4 py-3 text-right font-semibold">وضعیت</th>
                <th className="px-4 py-3 text-right font-semibold">وضعیت درگاه</th>
                <th className="px-4 py-3 text-right font-semibold">درگاه</th>
                <th className="px-4 py-3 text-right font-semibold">تاریخ</th>
                <th className="px-4 py-3 text-right font-semibold">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                    در حال بارگذاری…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                    پرداختی یافت نشد
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-slate-500">{rowNumber(page, limit, idx)}</td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-900">{row.orderNumber}</div>
                      <div className="text-xs text-slate-500">{row.customerName || row.customerPhone || '—'}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold tabular-nums">{formatMoney(row.amount)}</td>
                    <td className="px-4 py-3">{statusBadge(row.status)}</td>
                    <td className="px-4 py-3">{gatewayBadge(row.gatewayStatus)}</td>
                    <td className="px-4 py-3 text-slate-700">{row.provider}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDateTime(row.createdAt)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void openDetail(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        جزئیات
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
          <span>{total} پرداخت</span>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      </div>

      <Modal isOpen={!!detail} onClose={() => setDetail(null)} title="جزئیات پرداخت" size="lg">
        {detail ? (
          <div className="space-y-4" dir="rtl">
            <div className="grid gap-3 sm:grid-cols-2">
              <Info label="کد سفارش">
                <Link to="/orders" className="font-bold text-indigo-600 hover:underline">
                  {detail.orderNumber}
                </Link>
              </Info>
              <Info label="وضعیت">{statusBadge(detail.status)}</Info>
              <Info label="مبلغ (تومان)">{formatMoney(detail.amount)}</Info>
              <Info label="کارمزد (تومان)">{formatMoney(detail.fee)}</Info>
              <Info label="درگاه">{detail.provider}</Info>
              <Info label="وضعیت درگاه">{gatewayBadge(detail.gatewayStatus)}</Info>
              <Info label="Authority">
                <span className="break-all font-mono text-xs">{detail.authority || '—'}</span>
              </Info>
              <Info label="RefID">
                <span className="font-mono text-xs">{detail.refId || '—'}</span>
              </Info>
              <Info label="کارت">{detail.cardPan || '—'}</Info>
              <Info label="مشتری">
                {[detail.customerName, detail.customerPhone].filter(Boolean).join(' — ') || '—'}
              </Info>
              <Info label="ایجاد">{formatDateTime(detail.createdAt)}</Info>
              <Info label="تأیید">{formatDateTime(detail.verifiedAt)}</Info>
              <Info label="آخرین به‌روزرسانی">{formatDateTime(detail.updatedAt)}</Info>
            </div>

            {detail.gatewayPayload ? (
              <div>
                <div className={labelClass}>پاسخ درگاه</div>
                <pre className="max-h-48 overflow-auto rounded-lg bg-slate-900 p-3 text-left text-xs text-slate-100" dir="ltr">
                  {JSON.stringify(detail.gatewayPayload, null, 2)}
                </pre>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
              {detail.provider === 'zarinpal' ? (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void inquire(detail.id)}
                    className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-bold text-white hover:bg-slate-900 disabled:opacity-50"
                  >
                    <RefreshCw className="h-4 w-4" />
                    استعلام زرین‌پال
                  </button>
                  {detail.status !== 'completed' ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void verify(detail.id)}
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <ShieldCheck className="h-4 w-4" />
                      تأیید نهایی (Verify)
                    </button>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                بستن
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={unverifiedOpen}
        onClose={() => setUnverifiedOpen(false)}
        title="پرداخت‌های موفق تأییدنشده (زرین‌پال)"
        size="lg"
      >
        <div className="space-y-3" dir="rtl">
          <p className="text-sm text-slate-500">
            طبق API <span className="font-mono" dir="ltr">unVerified</span> زرین‌پال — تراکنش‌هایی که موفق شده‌اند ولی
            verify نشده‌اند.
          </p>
          {unverifiedLoading ? (
            <div className="py-8 text-center text-slate-500">در حال دریافت…</div>
          ) : unverified.length === 0 ? (
            <div className="py-8 text-center text-slate-500">موردی نیست</div>
          ) : (
            <div className="admin-table-scroll max-h-96 overflow-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-right">Authority</th>
                    <th className="px-3 py-2 text-right">مبلغ</th>
                    <th className="px-3 py-2 text-right">تاریخ درگاه</th>
                    <th className="px-3 py-2 text-right">سفارش محلی</th>
                    <th className="px-3 py-2 text-right">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {unverified.map((item) => (
                    <tr key={item.authority}>
                      <td className="px-3 py-2 font-mono text-xs">{item.authority}</td>
                      <td className="px-3 py-2 tabular-nums">{formatMoney(item.amount)}</td>
                      <td className="px-3 py-2 text-xs">{item.date || '—'}</td>
                      <td className="px-3 py-2">
                        {item.localPayment ? (
                          <span className="font-bold">{item.localPayment.orderNumber}</span>
                        ) : (
                          <span className="text-rose-600">ثبت نشده</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {item.localPayment && item.localPayment.status !== 'completed' ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void verify(item.localPayment!.id)}
                            className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            Verify
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/80 px-3 py-2">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm text-slate-900">{children}</div>
    </div>
  );
}
